import React, { useCallback, useEffect, useRef, useState } from "react";
import "../../assets/css/galleryCard.scss";
import "../../config";
import ZoomModal from "../Pages/ZoomModal";
import { useTranslation } from "../../contexts/TranslationContext";
import Utilities from "../../assets/js/Utilities";
import api from "../../utils/api";


function GetLinkImage(arrImages) {
  let img = arrImages.find((x) => x.IsStandardize === 1);

  if (!img) {
    img = arrImages.find((x) => x.Type === "SoleLeft" || x.Type === "SoleRight");
  }

  if (!img) img = arrImages[0];
  if (!img || !img.S3RelativePath) return "";

  let s3Path = img.S3RelativePath.startsWith("/") ? img.S3RelativePath.substring(1) : img.S3RelativePath;
  let _linkImgS3 = global.config.images3.link;
  if (!_linkImgS3) return "";

  return _linkImgS3.endsWith("/") ? `${_linkImgS3}${s3Path}` : `${_linkImgS3}/${s3Path}`;
}

const GalleryCard = ({ record, index, viewDetails, editFunc, deleteFunc, selectRecord, isSelected, hideActions, analyzeFunc }) => {
  // console.log("GalleryCard", record);
  const { language } = useTranslation();

  const imgRef = useRef(null);
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const [defaultIndex, setDefaultIndex] = useState(0);

  const [recordState, setRecordState] = useState(record);
  const [imgIsLoaded, setImgIsLoaded] = useState(false);

  // Sync recordState with record prop changes; reset lazy-load flag for new record
  useEffect(() => {
    setRecordState(record);
    setImgIsLoaded(false);
  }, [record]);


  const isContainSoleOrPrint = recordState.Images.some((x) =>
    x.Type !== global.config.Defines.ImageTypeKey.SHOE_OTHER
  );

  const [isFailStandardize, setIsFailStandardize] = useState(false);
  const [isStandardizing, setIsStandardizing] = useState(false);
  const isFetchingRef = useRef(false);
  const isMountedRef = useRef(false); // false until mount effect confirms it

  // Track mount status to prevent setState on unmounted component
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const limitTimeStandardize = 5 * 60 * 1000; // 5 minutes in milliseconds

  // Effect to check standardization status and timeout
  useEffect(() => {

    const shouldBeStandardizing = recordState.ImportStatus === 3 && isContainSoleOrPrint;

    if (shouldBeStandardizing && recordState && recordState.UploadedTicks) {
      const timeSinceUpload = Date.now() - Utilities.ConvertTicksToDateTime(recordState.UploadedTicks).getTime();

      if (timeSinceUpload > limitTimeStandardize) {
        // Standardization has been running too long, mark as failed
        setIsFailStandardize(true);
        setIsStandardizing(false);
      } else {
        // Still within time limit, keep standardizing
        setIsStandardizing(true);
        setIsFailStandardize(false);
      }
    } else {
      // Not in standardizing state
      setIsStandardizing(false);
      setIsFailStandardize(false);
    }
  }, [recordState, isContainSoleOrPrint, limitTimeStandardize]);

  const fetchToGetNewestUpdateOnRecord = useCallback(async () => {
    if (!recordState || !recordState.IDValue) return null;

    const tokenRequestAPI = localStorage.getItem("FWToken");
    if (!tokenRequestAPI) return null;

    try {
      const response = await api.post(`${global.config.api.domain}/fwiq-api/api/evidence/search`, {
        PageNumber: 1,
        PageSize: 1,
        IDs: [recordState.IDValue],
      });

      const result = response.data;

      if (result && Array.isArray(result.Records) && result.Records.length > 0) {
        return result.Records[0];
      }

      return null;

    } catch (error) {
      console.error('Error fetching newest record data:', error);
      return null;
    }

  }, [recordState]);

  // Effect to periodically check for timeout and fetch latest data while standardizing
  useEffect(() => {
    let timeoutInterval = null;

    if (isStandardizing && recordState && recordState.UploadedTicks) {
      // Check every 30 seconds for timeout and latest data
      timeoutInterval = setInterval(async () => {
        // Prevent multiple concurrent API calls
        if (isFetchingRef.current) {
          return;
        }

        // Guard: do not setState if component has unmounted
        if (!isMountedRef.current) {
          return;
        }

        try {
          isFetchingRef.current = true;

          // Fetch the newest data to check if standardization is complete
          const newestDataOfRecord = await fetchToGetNewestUpdateOnRecord();

          // Guard again after await in case component unmounted during the fetch
          if (!isMountedRef.current) return;

          if (newestDataOfRecord && newestDataOfRecord.ImportStatus !== recordState.ImportStatus) {
            // Update the record with the latest data
            setRecordState(newestDataOfRecord);

            // also make preview image update imgRef
            if (imgRef.current) {
              imgRef.current.src = GetLinkImage(newestDataOfRecord.Images);
            }

            // Check if standardization is complete (ImportStatus > 3)
            const newIsContainSoleOrPrint = newestDataOfRecord.Images.some((x) =>
              x.Type !== global.config.Defines.ImageTypeKey.SHOE_OTHER
            );

            const newShouldBeStandardizing = newestDataOfRecord.ImportStatus === 3 && newIsContainSoleOrPrint;

            setIsStandardizing(newShouldBeStandardizing);

            // If standardization is complete or failed, clear failure state
            if (newestDataOfRecord.ImportStatus > 3) {
              setIsFailStandardize(false);
            }

            // If no longer standardizing, we can exit the interval
            if (!newShouldBeStandardizing) {
              return; // The cleanup function will clear the interval
            }
          }

          // Check for timeout only if still in the same state
          const timeSinceUpload = Date.now() - Utilities.ConvertTicksToDateTime(recordState.UploadedTicks).getTime();

          if (timeSinceUpload > limitTimeStandardize) {
            setIsFailStandardize(true);
            setIsStandardizing(false);
          }
        } catch (error) {
          console.error('Error fetching latest record data:', error);
          if (!isMountedRef.current) return;
          // Continue with timeout check even if API call fails
          const timeSinceUpload = Date.now() - Utilities.ConvertTicksToDateTime(recordState.UploadedTicks).getTime();

          if (timeSinceUpload > limitTimeStandardize) {
            setIsFailStandardize(true);
            setIsStandardizing(false);
          }
        } finally {
          isFetchingRef.current = false;
        }
      }, 10000); // Check every 10 seconds
    }

    return () => {
      if (timeoutInterval) {
        clearInterval(timeoutInterval);
        timeoutInterval = null;
      }
    };
  }, [isStandardizing, recordState, limitTimeStandardize, fetchToGetNewestUpdateOnRecord]);

  const listScanners = localStorage.getItem("list_scanners") ? JSON.parse(localStorage.getItem("list_scanners")) : [];

  const ownerRecord = listScanners.find(s => s.ID === `${recordState?.User_IDValue}-${recordState?.User_ID_ExtValue}`);

  function GetPreviewImage(arrImages) {

    if (!arrImages || arrImages.length === 0) return null;

    let img = arrImages.find((x) => x.IsStandardize === 1);

    if (img) return img;

    if (!img) {
      img = arrImages.find((x) =>
        x.Type === global.config.Defines.ImageTypeKey.SHOE_SOLE_LEFT ||
        x.Type === global.config.Defines.ImageTypeKey.SHOE_SOLE_RIGHT);
    }

    if (!img) { img = arrImages[0]; }
    return img;
  }

  // Function to get the index of the preview image in the images array
  function GetPreviewImageIndex(arrImages) {
    if (!arrImages || arrImages.length === 0) return 0;

    // Find standardized image first
    let imgIndex = arrImages.findIndex((x) => x.IsStandardize === 1);

    if (imgIndex !== -1) return imgIndex;

    // Find sole images
    imgIndex = arrImages.findIndex((x) =>
      x.Type === global.config.Defines.ImageTypeKey.SHOE_SOLE_LEFT ||
      x.Type === global.config.Defines.ImageTypeKey.SHOE_SOLE_RIGHT);

    if (imgIndex !== -1) return imgIndex;

    // Default to first image
    return 0;
  }

  // Function to handle zoom icon click
  const handleZoomClick = (e) => {

    // let idPreview = GetPreviewImage(recordState.Images)?.IDValue;
    // e.stopPropagation();
    // const previewIndex = 
    setDefaultIndex(0);
    setIsZoomOpen(true);
  };

  const getLocalTimeFromGMTTick = (gmtTick) => {
    if (!gmtTick) return "";

    const localDate = Utilities.ConvertTicksToDateTime(gmtTick);

    return Utilities.FormatDateTime(localDate);
  };

  // const arrImages = record.Images?.map((img) => GetLinkImage([img])) || [];

  useEffect(() => {
    const imgElement = imgRef.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const lazyImage = entry.target;
          lazyImage.src = lazyImage.dataset.src; // Set data-src to src
          setImgIsLoaded(true);                  // React-controlled: survives re-renders
          observer.unobserve(lazyImage);         // Stop observing once loaded
        }
      },
      { threshold: 0.1 } // Trigger when 10% of the image is visible
    );

    if (imgElement) {
      observer.observe(imgElement);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  if (!recordState || !recordState.Images || recordState.Images.length === 0) {
    return (
      <div className="galleryCard empty">
        <div className="gallery-index">{language.gallery?.noImages || "No Images"}</div>
        <div className="preview-img">
          <i className="fa fa-image"></i>
          <span>{language.gallery?.noImagesAvailable || "No Images Available"}</span>
        </div>
        <div className="gallery-data">
          <div className="row-data">{language.gallery?.noData || "No Data"}</div>
        </div>
        {!hideActions && (
          <div className="gallery-actions">
            <i className="fa fa-info" onClick={viewDetails} data-type="tooltip" alt={language.gallery?.details || "Details"}></i>
            <i className="fa fa-pencil" onClick={editFunc} data-type="tooltip" alt={language.gallery?.edit || "Edit"}></i>
            <i className="fa fa-trash" onClick={deleteFunc} data-type="tooltip" alt={language.gallery?.delete || "Delete"}></i>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className={`galleryCard ${isSelected ? "selected" : ""} ${isStandardizing ? "standardizing" : ""} ${isFailStandardize ? "failed-standardize" : ""}`}
        onDoubleClick={viewDetails} onClick={selectRecord}>
        <div className={
          "gallery-index" +
          (index >= 10000 ? " over-10k"
            : index >= 1000 ? " over-1000"
              : index >= 100 ? " over-100"
                : "")
        }>
          {index?.toLocaleString() || 0} {/* Format the number for easier readability */}
        </div>
        <div className="preview-img">
          <img
            ref={imgRef}
            className={`lazyImg${imgIsLoaded ? "" : " loading"}`}
            image-id={GetPreviewImage(recordState.Images)?.IDValue}
            data-src={GetLinkImage(recordState.Images)} // Use data-src for lazy loading
            alt="Gallery"
          />
          <div className="zoom-icon">
            <i className="fa fa-search-plus" aria-hidden="true" onClick={handleZoomClick}></i>
          </div>

          {isStandardizing && (
            <div className="standardizing-overlay">
              <div className="spinner">
                <i className="fa fa-spinner fa-spin"></i>
              </div>
              <div className="standardizing-text">{language.gallery?.standardizing || "Standardizing..."}</div>
            </div>
          )}

          {isFailStandardize && (
            <div className="failed-standardize-overlay">
              <div className="error-icon">
                <i className="fa fa-exclamation-triangle" aria-hidden="true"></i>
              </div>
              <div className="failed-standardize-text">{language.gallery?.standardizeFailed || "Standardization Failed"}</div>
            </div>
          )}
        </div>
        <div className="gallery-data">
          {recordState.Category_ID === undefined || recordState.Category_ID === global.config.Defines.CategoryID.NewShoe ? (
            <>
              <div className="row-data">{recordState.Make}</div>
              <div className="row-data">{recordState.ShoeType}</div>
              <div className="row-data">{recordState.Model}</div>
              <div className="row-data">{recordState.ProductCode}</div>
            </>
          ) : (
            <>
              <div className="row-data">{recordState.ImpressionInfo.CaseIncident}</div>
              <div className="row-data">{recordState.ImpressionInfo.ImpressionNumber}</div>
              <div className="row-data">
                {/* <div className="row-data-title">
                  {language.gallery?.locationOfIncident || "Location of Incident"}
                </div> */}
                <div className="row-data-value">
                  <i className="fa fa-map-marker"></i>
                  <span> {recordState.ImpressionInfo.LocationOfIncident}</span>
                </div>
              </div>
              <div className="row-data">
                {/* <div className="row-data-title">
                  {language.gallery?.uploadedOn || "Uploaded On"}
                </div> */}
                <div className="row-data-value">
                  <i className="fa fa-upload"></i>
                  <span>
                    {getLocalTimeFromGMTTick(recordState.UploadedTicks)}
                  </span>
                </div>
              </div>
              {ownerRecord && (
                <div className="row-data">
                  <div className="row-data-value">
                    <i className="fa fa-user"></i>
                    <span>
                      {ownerRecord ? ownerRecord.Value : ""}
                    </span>
                  </div>
                </div>
              )}
            </>
          )
          }
        </div>
        {!hideActions && (
          <div className="gallery-actions">
            {/* Always show Details icon */}
            <i className="fa fa-info" onClick={(e) => {
              e.stopPropagation();
              viewDetails();
            }} data-type="tooltip" alt={language.gallery?.details || "Details"}></i>

            {/* Only evidence galleries have Analyze, Edit and Delete */}
            {recordState.Category_ID !== global.config.Defines.CategoryID.NewShoe && (
              <>
                <i className="fa fa-search" onClick={(e) => {
                  e.stopPropagation();
                  if (analyzeFunc) {
                    analyzeFunc(recordState);
                  }
                }} data-type="tooltip" alt={language.gallery?.analyze || "Analyze"}></i>
                <i className="fa fa-pencil" onClick={(e) => {
                  e.stopPropagation();
                  editFunc(e);
                }} data-type="tooltip" alt={language.gallery?.edit || "Edit"}></i>
                <i className="fa fa-trash" onClick={(e) => {
                  e.stopPropagation();
                  deleteFunc(e);
                }} data-type="tooltip" alt={language.gallery?.delete || "Delete"}></i>
              </>
            )}
          </div>
        )}
      </div >

      <ZoomModal images={recordState.Images} defaultIndex={defaultIndex} isOpen={isZoomOpen} onClose={() => setIsZoomOpen(false)} />
    </>
  );
};

export default GalleryCard;